import React, { useCallback, useRef, useState } from 'react';
import RegistryPdfSummary from '../components/RegistryPdf/RegistryPdfSummary';
import { useUser } from '../contexts/UserContext';
import { RegistryPdfDocument } from '../types/registryPdf';
import { buildRegistryPdf, loadReportFonts } from '../utils/registryPdfBuilder';

interface PdfExport {
    id: number;
    generatedAt: Date;
    document: RegistryPdfDocument;
}


export const getPdfButtonLabel = (progress: number | null): string => {
    return progress === null ? 'Скачать PDF' : `Формируем PDF… ${progress}%`;
};


// Выгрузка в PDF: страница передаёт в startPdfExport функцию с данными отчёта и выводит у себя pdfReport
// (скрытую сводку, которую снимает html2canvas), pdfProgress процент готовности, null выгрузка не идёт
export const useRegistryPdfExport = () => {
    const { user } = useUser();
    const [pdfExport, setPdfExport] = useState<PdfExport | null>(null);
    const [pdfProgress, setPdfProgress] = useState<number | null>(null);
    // ref, а не state, повторный клик приходит раньше, чем кнопка перерисуется
    const isExportingRef = useRef(false);
    const startedExportIdRef = useRef<number | null>(null);

    const getUserName = () => {
        if (!user) return '—';
        const initials = (user.first_name || '').split(' ').filter(Boolean).map(name => `${name[0].toUpperCase()}.`).join('');
        if (user.last_name) return `${user.last_name} ${initials}`.trim();
        return user.first_name || user.username;
    };

    const startPdfExport = async (getDocument: () => RegistryPdfDocument) => {
        if (isExportingRef.current) return;
        isExportingRef.current = true;
        setPdfProgress(0);
        try {
            // Шрифт нужен до отрисовки сводки от него зависит раскладка по листам
            await loadReportFonts();
            setPdfExport({ id: Date.now(), generatedAt: new Date(), document: getDocument() });
        } catch (err) {
            console.error('Ошибка при подготовке PDF:', err);
            alert('Ошибка при создании PDF');
            setPdfProgress(null);
            isExportingRef.current = false;
        }
    };

    const handleSummaryReady = useCallback(async (summaryPages: HTMLElement[]) => {
        // Сводка может сообщить о готовности повторно (перемонтирование, Fast Refresh) PDF скачиваем один раз
        if (!pdfExport || startedExportIdRef.current === pdfExport.id) return;
        startedExportIdRef.current = pdfExport.id;
        try {
            const pdf = await buildRegistryPdf({ summaryPages, sections: pdfExport.document.sections, onProgress: setPdfProgress });
            pdf.save(pdfExport.document.fileName);
        } catch (err) {
            console.error('Ошибка при создании PDF:', err);
            alert('Ошибка при создании PDF');
        } finally {
            setPdfExport(null);
            setPdfProgress(null);
            isExportingRef.current = false;
        }
    }, [pdfExport]);

    const pdfReport = pdfExport ? (
        <RegistryPdfSummary
            summary={pdfExport.document.summary}
            userName={getUserName()}
            generatedAt={pdfExport.generatedAt}
            onReady={handleSummaryReady}
        />
    ) : null;

    return { pdfProgress, startPdfExport, pdfReport };
};
